import React, { useState } from 'react';
import { cn } from '../lib/utils';
import { useTheme } from '../App';
import { motion } from 'framer-motion';

export default function CodeBlock({ htmlCode, fileName = "landing-page.html" }) {
  const { darkMode } = useTheme();
  const [copied, setCopied] = useState(false);


  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(htmlCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  const handlePreview = () => {
    try {
      // Create a blob with the HTML content
      const blob = new Blob([htmlCode], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      
      // Open in new tab
      const newWindow = window.open(url, '_blank');
      
      // Fallback if popup was blocked
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        // If popup blocked, offer download instead
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        alert('Popup blocked! The landing page has been downloaded instead. Open the file in your browser to view it.');
      }
      
      // Clean up the blob URL after a short delay
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      console.error('Failed to preview code:', err);
      alert('Failed to open preview. You can copy the code and save it as an HTML file to view it.');
    }
  };

  return (
    <>
      <style>{`
        .code-border-anim {
          position: absolute;
          top: -1px;
          left: -1px;
          width: calc(100% + 2px);
          height: calc(100% + 2px);
          border-radius: 1rem;
          overflow: hidden;
          pointer-events: none;
        }
        .code-border-anim::before {
          content: "";
          position: absolute;
          top: 50%;
          left: 50%;
          width: 200%;
          height: 10rem;
          background: linear-gradient(
            0deg,
            hsla(0,0%,100%,0) 0%,
            hsl(238,100%,70%) 40%,
            hsl(262,100%,70%) 60%,
            hsla(0,0%,40%,0) 100%
          );
          transform: translate(-50%, -50%) rotate(0deg);
          transform-origin: left;
          animation: border-rotate 8s linear infinite;
          z-index: 2;
          pointer-events: none;
        }
        @keyframes border-rotate {
          to {
            transform: translate(-50%, -50%) rotate(360deg);
          }
        }
      `}</style>
      
      <div className="relative w-full max-w-4xl rounded-xl p-0.5">
        <div className="code-border-anim" />
        <div className={cn(
          "rounded-xl p-6 shadow-[0px_-16px_24px_0px_rgba(255,255,255,0.25)_inset]",
          darkMode
            ? "bg-[radial-gradient(at_88%_40%,#1e1b2e_0,transparent_85%),radial-gradient(at_49%_30%,#1e1b2e_0,transparent_85%),radial-gradient(at_14%_26%,#1e1b2e_0,transparent_85%),radial-gradient(at_0%_64%,#3b82f6_0,transparent_85%),radial-gradient(at_41%_94%,#8b5cf6_0,transparent_85%),radial-gradient(at_100%_99%,#1e40af_0,transparent_85%)]"
            : "bg-[radial-gradient(at_88%_40%,#f8fafc_0,transparent_85%),radial-gradient(at_49%_30%,#f1f5f9_0,transparent_85%),radial-gradient(at_14%_26%,#e2e8f0_0,transparent_85%),radial-gradient(at_0%_64%,#dbeafe_0,transparent_85%),radial-gradient(at_41%_94%,#e9d5ff_0,transparent_85%),radial-gradient(at_100%_99%,#f0f9ff_0,transparent_85%)]"
        )}>
          <div className="flex items-center justify-between pb-4">
            <span className={cn(
              "text-base font-semibold",
              darkMode ? "text-white" : "text-gray-800"
            )}>
              {fileName}
            </span>
            <div className="flex items-center gap-3">
              <motion.button
                onClick={handlePreview}
                className="rounded-lg px-4 py-2 text-sm font-medium transition bg-gradient-to-r from-green-500 to-teal-500 text-white hover:from-green-600 hover:to-teal-600"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                View
              </motion.button>
              <motion.button
                onClick={handleCopy}
                className={cn(
                  "rounded-lg px-4 py-2 text-sm font-medium transition",
                  copied
                    ? "bg-green-500 text-white"
                    : "bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:from-blue-600 hover:to-purple-600"
                )}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {copied ? "Copied!" : "Copy Code"}
              </motion.button>
            </div>
          </div>
          <div className={cn(
            "max-h-96 overflow-auto rounded-lg p-4",
            darkMode 
              ? "bg-gray-900/50 border border-gray-700"
              : "bg-white/80 border border-gray-300"
          )}>
            <pre className={cn(
              "m-0 text-sm leading-relaxed whitespace-pre-wrap break-words",
              darkMode ? "text-gray-200" : "text-gray-800"
            )}>
              <code className="font-mono">{htmlCode}</code>
            </pre>
          </div>
        </div>
      </div>
    </>
  );
}